import React, { useState } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	InputAdornment,
	IconButton,
	Typography,
	Switch,
	FormControlLabel,
	FormGroup,
	Checkbox,
	Tooltip,
	CircularProgress,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastCompanyError from "./companyErrors";

const useStyles = makeStyles((theme) => ({
	sectionTitle: {
		fontSize: "0.75rem",
		fontWeight: 700,
		textTransform: "uppercase",
		letterSpacing: "0.06em",
		color: theme.custom.textSecondary,
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(0.5),
	},
	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},
	btnWrapper: {
		position: "relative",
	},
	buttonProgress: {
		color: theme.palette.primary.main,
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	statusRow: {
		marginTop: theme.spacing(1),
	},
}));

const toNullableInt = (value) =>
	value === "" || value === null || value === undefined
		? null
		: Number(value);

// "whatsapp" e o produto base e acompanha toda empresa; os demais sao opcionais.
const OPTIONAL_MODULES = ["financeiro", "flydesk"];

// Icone de ajuda com a explicacao do campo. Vai dentro do proprio campo, para
// nao empurrar o layout nem competir com o texto de apoio abaixo dele.
const HelpAdornment = ({ text }) => (
	<InputAdornment position="end">
		<Tooltip
			title={<span style={{ whiteSpace: "pre-line" }}>{text}</span>}
			placement="top"
			arrow
			interactive
		>
			<HelpOutlineIcon
				style={{ fontSize: 18, color: "#94A3B8", cursor: "help" }}
			/>
		</Tooltip>
	</InputAdornment>
);

const CompanyModal = ({ open, onClose, company, onSaved }) => {
	const classes = useStyles();
	const isEdit = Boolean(company);
	const isDefaultCompany = company?.id === 1;
	const [showPassword, setShowPassword] = useState(false);

	const limitSchema = Yup.number()
		.transform((value, originalValue) =>
			originalValue === "" || originalValue === null ? null : value
		)
		.nullable()
		.integer(i18n.t("companies.modal.validation.integer"))
		.min(1, i18n.t("companies.modal.validation.limitMin"));

	const CompanySchema = Yup.object().shape({
		name: Yup.string()
			.min(2, i18n.t("companies.modal.validation.tooShort"))
			.max(100, i18n.t("companies.modal.validation.tooLong"))
			.required(i18n.t("companies.modal.validation.required")),
		adminName: Yup.string().max(100, i18n.t("companies.modal.validation.tooLong")),
		email: isEdit
			? Yup.string()
			: Yup.string()
					.email(i18n.t("companies.modal.validation.invalidEmail"))
					.required(i18n.t("companies.modal.validation.required")),
		password: isEdit
			? Yup.string()
			: Yup.string()
					.min(5, i18n.t("companies.modal.validation.tooShort"))
					.max(50, i18n.t("companies.modal.validation.tooLong"))
					.required(i18n.t("companies.modal.validation.required")),
		maxUsers: limitSchema,
		maxConnections: limitSchema,
		maxDevices: limitSchema,
	});

	const initialValues = {
		name: company?.name || "",
		adminName: "",
		email: "",
		password: "",
		maxUsers: company?.maxUsers ?? "",
		maxConnections: company?.maxConnections ?? "",
		maxDevices: company?.maxDevices ?? "",
		status: company ? company.status !== false : true,
		modules: Array.isArray(company?.modules) ? company.modules : ["whatsapp"],
	};

	const handleClose = () => {
		setShowPassword(false);
		onClose();
	};

	const handleSave = async (values) => {
		const limits = {
			maxUsers: toNullableInt(values.maxUsers),
			maxConnections: toNullableInt(values.maxConnections),
			maxDevices: toNullableInt(values.maxDevices),
		};
		// whatsapp entra sempre: o backend tambem forca, isso so evita mandar
		// um payload que nao reflete o que a tela mostra
		const modules = Array.from(new Set(["whatsapp", ...values.modules]));
		try {
			if (isEdit) {
				await api.put(`/companies/${company.id}`, {
					name: values.name,
					status: values.status,
					modules,
					...limits,
				});
			} else {
				await api.post("/companies", {
					name: values.name,
					adminName: values.adminName || values.name,
					email: values.email,
					password: values.password,
					modules,
					...limits,
				});
			}
			toast.success(i18n.t("companies.toasts.saved"));
			onSaved();
			handleClose();
		} catch (err) {
			toastCompanyError(err);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="xs"
			fullWidth
			scroll="paper"
		>
			<DialogTitle>
				{isEdit
					? i18n.t("companies.modal.editTitle")
					: i18n.t("companies.modal.addTitle")}
			</DialogTitle>
			<Formik
				initialValues={initialValues}
				enableReinitialize={true}
				validationSchema={CompanySchema}
				onSubmit={async (values, actions) => {
					await handleSave(values);
					actions.setSubmitting(false);
				}}
			>
				{({ values, touched, errors, isSubmitting, setFieldValue }) => (
					<Form>
						<DialogContent dividers>
							<Field
								as={TextField}
								label={i18n.t("companies.modal.name")}
								name="name"
								error={touched.name && Boolean(errors.name)}
								helperText={touched.name && errors.name}
								fullWidth
								margin="dense"
								variant="outlined"
								autoFocus
							/>

							{isEdit && (
								<div className={classes.statusRow}>
									<Tooltip
										title={
											isDefaultCompany
												? i18n.t("companies.table.cannotDeactivateDefault")
												: ""
										}
									>
										<FormControlLabel
											control={
												<Switch
													color="primary"
													checked={values.status}
													disabled={isDefaultCompany}
													onChange={(e) =>
														setFieldValue("status", e.target.checked)
													}
												/>
											}
											label={i18n.t("companies.modal.status")}
										/>
									</Tooltip>
								</div>
							)}

							{!isEdit && (
								<>
									<Typography className={classes.sectionTitle}>
										{i18n.t("companies.modal.adminSection")}
									</Typography>
									<Field
										as={TextField}
										label={i18n.t("companies.modal.adminName")}
										name="adminName"
										error={touched.adminName && Boolean(errors.adminName)}
										helperText={
											(touched.adminName && errors.adminName) ||
											i18n.t("companies.modal.adminNameHelper")
										}
										fullWidth
										margin="dense"
										variant="outlined"
									/>
									<Field
										as={TextField}
										label={i18n.t("companies.modal.email")}
										name="email"
										type="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										fullWidth
										margin="dense"
										variant="outlined"
									/>
									<Field
										as={TextField}
										label={i18n.t("companies.modal.password")}
										name="password"
										type={showPassword ? "text" : "password"}
										error={touched.password && Boolean(errors.password)}
										helperText={touched.password && errors.password}
										fullWidth
										margin="dense"
										variant="outlined"
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														size="small"
														aria-label="toggle password visibility"
														onClick={() => setShowPassword((e) => !e)}
													>
														{showPassword ? <VisibilityOff /> : <Visibility />}
													</IconButton>
												</InputAdornment>
											),
										}}
									/>
								</>
							)}

							<Typography className={classes.sectionTitle}>
								{i18n.t("companies.modal.modulesSection")}
							</Typography>
							<FormGroup>
								<Tooltip
									title={i18n.t("companies.modal.moduleWhatsappHelper")}
								>
									<FormControlLabel
										control={
											<Checkbox checked disabled color="primary" />
										}
										label={i18n.t("companies.modal.moduleWhatsapp")}
									/>
								</Tooltip>
								{OPTIONAL_MODULES.map((mod) => (
									<FormControlLabel
										key={mod}
										control={
											<Checkbox
												color="primary"
												checked={values.modules.includes(mod)}
												onChange={(e) =>
													setFieldValue(
														"modules",
														e.target.checked
															? [...values.modules, mod]
															: values.modules.filter((m) => m !== mod)
													)
												}
											/>
										}
										label={i18n.t(`companies.modal.module_${mod}`)}
									/>
								))}
							</FormGroup>

							<Typography className={classes.sectionTitle}>
								{i18n.t("companies.modal.limitsSection")}
							</Typography>
							<div className={classes.multFieldLine}>
								<Field
									as={TextField}
									label={i18n.t("companies.modal.maxUsers")}
									name="maxUsers"
									type="number"
									inputProps={{ min: 1 }}
									InputProps={{
										endAdornment: (
											<HelpAdornment
												text={i18n.t("companies.modal.maxUsersHelp")}
											/>
										),
									}}
									error={touched.maxUsers && Boolean(errors.maxUsers)}
									helperText={
										(touched.maxUsers && errors.maxUsers) ||
										i18n.t("companies.modal.unlimitedHelper")
									}
									fullWidth
									margin="dense"
									variant="outlined"
								/>
								<Field
									as={TextField}
									label={i18n.t("companies.modal.maxConnections")}
									name="maxConnections"
									type="number"
									inputProps={{ min: 1 }}
									InputProps={{
										endAdornment: (
											<HelpAdornment
												text={i18n.t("companies.modal.maxConnectionsHelp")}
											/>
										),
									}}
									error={
										touched.maxConnections && Boolean(errors.maxConnections)
									}
									helperText={
										(touched.maxConnections && errors.maxConnections) ||
										i18n.t("companies.modal.unlimitedHelper")
									}
									fullWidth
									margin="dense"
									variant="outlined"
								/>
							</div>

							{/* So faz sentido limitar PCs se a empresa tem o Flydesk */}
							{values.modules.includes("flydesk") && (
								<Field
									as={TextField}
									label={i18n.t("companies.modal.maxDevices")}
									name="maxDevices"
									type="number"
									inputProps={{ min: 1 }}
									InputProps={{
										endAdornment: (
											<HelpAdornment
												text={i18n.t("companies.modal.maxDevicesHelp")}
											/>
										),
									}}
									error={touched.maxDevices && Boolean(errors.maxDevices)}
									helperText={
										(touched.maxDevices && errors.maxDevices) ||
										i18n.t("companies.modal.maxDevicesHelper")
									}
									fullWidth
									margin="dense"
									variant="outlined"
								/>
							)}
						</DialogContent>
						<DialogActions>
							<Button
								onClick={handleClose}
								color="secondary"
								disabled={isSubmitting}
								variant="outlined"
							>
								{i18n.t("companies.modal.cancel")}
							</Button>
							<Button
								type="submit"
								color="primary"
								variant="contained"
								disabled={isSubmitting}
								className={classes.btnWrapper}
							>
								{i18n.t("companies.modal.save")}
								{isSubmitting && (
									<CircularProgress
										size={24}
										className={classes.buttonProgress}
									/>
								)}
							</Button>
						</DialogActions>
					</Form>
				)}
			</Formik>
		</Dialog>
	);
};

export default CompanyModal;
